/**
 * Brand Subscriptions Section — onglet "Modules" du BrandDrawer super-admin.
 *
 * Modele d'agregation upward (cf US-MAC) :
 *  - **SITE / POS / CENTRAL** : 3 toggles ON/OFF + champ screenLimit pour BDISPLAY.
 *    On active/desactive la souscription directement sur la brand.
 *  - **GROUP** : pas de sub directe — affichage agrege en lecture seule.
 *    Un module est "actif" sur le GROUP si au moins un de ses enfants l'a actif.
 *    L'admin doit gerer les souscriptions au niveau site.
 */

import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { Button, Input, Loader, Badge } from '@/components/ui';
import {
  ALL_PRODUCTS,
  PRODUCT_LABELS,
  Product,
  type EffectiveSubscription,
} from '@bill/core-types';
import {
  useActivateSubscription,
  useBrandSubscriptions,
  useDeactivateSubscription,
  useSetBdisplayScreenLimit,
  useSetSubscriptionDemo,
} from '../hooks/useAdminSubscriptions';

type BrandType = 'GROUP' | 'POINT_OF_SALE' | 'CENTRAL';

interface BrandSubscriptionsSectionProps {
  brandId: string;
  brandType: BrandType;
}

export function BrandSubscriptionsSection({ brandId, brandType }: BrandSubscriptionsSectionProps) {
  const { data, isLoading } = useBrandSubscriptions(brandId);
  const activate = useActivateSubscription(brandId);
  const deactivate = useDeactivateSubscription(brandId);
  const setScreenLimit = useSetBdisplayScreenLimit(brandId);
  const setDemo = useSetSubscriptionDemo(brandId);

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader size="sm" />
        <span className="text-sm text-neutral-500">Chargement des souscriptions...</span>
      </div>
    );
  }

  const isGroup = brandType === 'GROUP';
  const subsByProduct = new Map(data.subscriptions.map((s) => [s.product, s]));

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 p-3 rounded-lg bg-info-50 border border-info-200 text-sm text-info-800">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          {isGroup
            ? "Cet etablissement est un groupe : les souscriptions se gerent au niveau de chaque site enfant. Un module est considere comme actif sur le groupe des qu'au moins un site enfant l'a active."
            : "Activez ou desactivez les modules SaaS pour cet etablissement. La licence ecrans BDisplay se definit a l'activation."}
        </div>
      </div>

      {ALL_PRODUCTS.map((product) => {
        const sub = subsByProduct.get(product);
        if (!sub) return null;
        if (isGroup) {
          return <GroupModuleRow key={product} product={product} sub={sub} />;
        }
        return (
          <SiteModuleRow
            key={product}
            product={product}
            sub={sub}
            screenLimit={data.bdisplayScreenLimit}
            isMutating={
              activate.isPending ||
              deactivate.isPending ||
              setScreenLimit.isPending ||
              setDemo.isPending
            }
            onToggle={(next, screenLimitInput) => {
              if (next) {
                activate.mutate({
                  product,
                  ...(product === Product.BDISPLAY
                    ? { bdisplayScreenLimit: screenLimitInput ?? null }
                    : {}),
                });
              } else {
                deactivate.mutate(product);
              }
            }}
            onScreenLimitChange={(limit) => setScreenLimit.mutate(limit)}
            onToggleDemo={(nextDemo) => setDemo.mutate({ product, isDemo: nextDemo })}
          />
        );
      })}
    </div>
  );
}

// ─── GROUP : affichage agrege en lecture seule ───────────────────────────────

function GroupModuleRow({
  product,
  sub,
}: {
  product: Product;
  sub: EffectiveSubscription;
}) {
  const activeCount = sub.aggregatedFromChildBrandIds?.length ?? 0;
  return (
    <div className="border border-neutral-200 rounded-xl p-4 bg-white">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="font-semibold text-dark-500">{PRODUCT_LABELS[product]}</div>
          {sub.effective ? (
            <Badge variant="success">
              Actif sur {activeCount} site{activeCount > 1 ? 's' : ''}
            </Badge>
          ) : (
            <Badge variant="neutral">Aucun site actif</Badge>
          )}
        </div>
        <span className="text-xs text-neutral-500">Geree au niveau site</span>
      </div>
    </div>
  );
}

// ─── SITE / POS / CENTRAL : toggles ON/OFF + screenLimit ─────────────────────

interface SiteModuleRowProps {
  product: Product;
  sub: EffectiveSubscription;
  screenLimit: number | null;
  isMutating: boolean;
  onToggle: (next: boolean, screenLimit?: number | null) => void;
  onScreenLimitChange: (limit: number | null) => void;
  onToggleDemo: (nextDemo: boolean) => void;
}

function SiteModuleRow({
  product,
  sub,
  screenLimit,
  isMutating,
  onToggle,
  onScreenLimitChange,
  onToggleDemo,
}: SiteModuleRowProps) {
  const [pendingLimit, setPendingLimit] = useState<string>(
    screenLimit !== null ? String(screenLimit) : '',
  );
  const [pendingActivationLimit, setPendingActivationLimit] = useState<string>('');

  useEffect(() => {
    setPendingLimit(screenLimit !== null ? String(screenLimit) : '');
  }, [screenLimit]);

  const isActive = sub.effective;

  return (
    <div className="border border-neutral-200 rounded-xl p-4 bg-white">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="font-semibold text-dark-500">{PRODUCT_LABELS[product]}</div>

          {!isActive && sub.disabledAt && (
            <Badge variant="neutral">
              Desactive le {new Date(sub.disabledAt).toLocaleDateString('fr-FR')}
            </Badge>
          )}
        </div>

        <Button
          variant={isActive ? 'danger' : 'primary'}
          size="sm"
          disabled={isMutating}
          onClick={() => {
            if (isActive) {
              onToggle(false);
            } else {
              const parsed =
                product === Product.BDISPLAY ? parseLimit(pendingActivationLimit) : null;
              onToggle(true, parsed);
            }
          }}
        >
          {isActive ? 'Desactiver' : 'Activer'}
        </Button>
      </div>

      {!isActive && product === Product.BDISPLAY && (
        <div className="mt-3 pt-3 border-t border-neutral-100">
          <Input
            label="Limite d'ecrans (optionnel)"
            type="number"
            min={0}
            max={10000}
            value={pendingActivationLimit}
            onChange={(e) => setPendingActivationLimit(e.target.value)}
            placeholder="Vide = illimite"
            helperText="Nombre max d'ecrans appairables sur cet etablissement."
          />
        </div>
      )}

      {isActive && product === Product.BDISPLAY && (
        <div className="mt-3 pt-3 border-t border-neutral-100">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Limite d'ecrans"
                type="number"
                min={0}
                max={10000}
                value={pendingLimit}
                onChange={(e) => setPendingLimit(e.target.value)}
                placeholder="Vide = illimite"
                helperText="Vide pour aucune limite."
              />
            </div>
            <Button
              variant="secondary"
              size="md"
              disabled={isMutating}
              onClick={() => onScreenLimitChange(parseLimit(pendingLimit))}
            >
              Mettre a jour
            </Button>
          </div>
        </div>
      )}

      {product === Product.BDISPLAY && (sub.effective || sub.disabledAt) && (
        <div className="mt-3 pt-3 border-t border-neutral-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-dark-500">Mode demonstration</div>
            <div className="text-xs text-neutral-500">
              Affiche un bandeau "Mode demo" sur tous les ecrans pairs de cette
              brand. A reserver aux deploiements commerciaux.
            </div>
          </div>
          <Button
            variant={sub.isDemo ? 'danger' : 'secondary'}
            size="sm"
            disabled={isMutating}
            onClick={() => {
              const next = !sub.isDemo;
              const message = next
                ? `Activer le mode demo sur ${PRODUCT_LABELS[product]} ?\n\nUn bandeau "Mode demo" apparaitra sur TOUS les ecrans pairs de cette brand.`
                : `Desactiver le mode demo sur ${PRODUCT_LABELS[product]} ?\n\nLe bandeau disparaitra sur tous les ecrans pairs.`;
              if (window.confirm(message)) {
                onToggleDemo(next);
              }
            }}
          >
            {sub.isDemo ? 'Desactiver demo' : 'Activer demo'}
          </Button>
        </div>
      )}
    </div>
  );
}

function parseLimit(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}
